import { Flex, Slide, Stack, Text, useBreakpointValue, useTheme } from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, redirect, useLocation, useNavigate } from 'react-router-dom'

import Logo from 'assets/logo.svg'
import ButtonNavigation from 'components/button-navigation'
import Input from 'components/input'
import NotificationError from 'components/notification-error'
import { splashContainerFilterProps, splashContainerProps } from 'components/splash'
import { useUserContext } from 'contexts/user-context'

export default function Login() {
  const theme = useTheme()
  const { session, signIn } = useUserContext()
  if (session) redirect('/')
  const { state } = useLocation()
  const navigate = useNavigate()
  const isSmallViewport = useBreakpointValue({ base: true, md: false })
  const [username, setUsername] = useState(state?.username ?? '')
  const [password, setPassword] = useState('')

  const signInMutation = useMutation({
    mutationFn: signIn,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (response: any) => {
      if (response.challengeName && response.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // user is valid, but needs to set a non-temporary password
        navigate('/reset-password', { state: { user: JSON.parse(JSON.stringify(response)), username: username } })
      } else {
        navigate('/')
      }
    },
  })

  const onSubmit = () => {
    signInMutation.mutateAsync({ username, password })
  }

  return (
    <Flex align="center" {...splashContainerProps}>
      <Flex w="100%" h="100vh" align="center" direction="column" {...splashContainerFilterProps}>
        <Slide direction="bottom" in>
          <Flex w="100%" h="100%" align="center" justify="center">
            <Flex direction="column" w={isSmallViewport ? '100%' : { base: '60%', lg: '40%' }} h="100vh">
              <Flex flex={isSmallViewport ? 1 : 0.5} p="36px" align="center" justify="center">
                <img src={Logo} width="360px" height="63px" />
              </Flex>
              <Stack
                direction="column"
                justify={isSmallViewport ? 'space-between' : 'unset'}
                spacing={{ base: '36px', md: '24px' }}
                flex={2}
                p="36px"
                borderTopRadius="16px"
                borderBottomRadius={isSmallViewport ? '0px' : '16px'}
                background={theme.colors.white}
              >
                <Stack w="100%" direction="column" spacing={{ base: 'inherit', md: '48px' }}>
                  <Flex>
                    <Text as="h1" textStyle="H1">
                      Sign In
                    </Text>
                  </Flex>
                  <Flex direction="column">
                    <Flex w="100%">
                      <Input
                        label={{ text: 'Username' }}
                        value={username}
                        isError={signInMutation.isError}
                        isDisabled={signInMutation.isLoading}
                        onChange={evt => setUsername(evt.target.value)}
                        onKeypress={evt => {
                          if (username && password && evt.key === 'Enter') onSubmit()
                        }}
                      />
                    </Flex>
                    <Flex w="100%" mt="16px">
                      <Input
                        type="password"
                        label={{ text: 'Password' }}
                        value={password}
                        isError={signInMutation.isError}
                        isDisabled={signInMutation.isLoading}
                        onChange={evt => setPassword(evt.target.value)}
                        onKeypress={evt => {
                          if (username && password && evt.key === 'Enter') onSubmit()
                        }}
                      />
                    </Flex>
                    <Flex w="100%" mt="2px" justify="flex-end">
                      <Link to="/forgot-password" state={{ username }}>
                        <Text
                          py="4px"
                          color={theme.baseColors.primary.main}
                          fontFamily={theme.fontFamilies.inter}
                          fontSize="sm"
                          textAlign="right"
                        >
                          Forgot password?
                        </Text>
                      </Link>
                    </Flex>
                  </Flex>
                  <Stack w="100%" direction="column" spacing="8px">
                    {signInMutation.isError && <NotificationError text="Your username or password is incorrect." />}
                    <ButtonNavigation
                      w="100%"
                      type="progress"
                      text="Sign In"
                      isDisabled={!username || !password || signInMutation.isLoading}
                      isLoading={signInMutation.isLoading}
                      onClick={onSubmit}
                    />
                  </Stack>
                </Stack>
                {isSmallViewport && <Flex />}
              </Stack>
              {!isSmallViewport && <Flex flex={1} />}
            </Flex>
          </Flex>
        </Slide>
      </Flex>
    </Flex>
  )
}
